import { useState } from 'react';
import { Logo } from './Logo.js';

interface OnboardingProps {
  onComplete: () => void;
  userEmail: string;
}

const STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to AAS Shield',
    subtitle: 'Your workplace communication guardian',
  },
  {
    id: 'how-shield',
    title: 'Shield Mode',
    subtitle: 'Detect toxic emails you receive',
  },
  {
    id: 'how-sender',
    title: 'Sender Mode',
    subtitle: 'Check your drafts before sending',
  },
  {
    id: 'install',
    title: 'Install where you work',
    subtitle: 'Works with Gmail, Outlook, and any browser',
  },
  {
    id: 'team',
    title: 'Better together',
    subtitle: 'Invite your team for group discounts',
  },
  {
    id: 'ready',
    title: "You're all set!",
    subtitle: 'Start analyzing your first email',
  },
] as const;

export function Onboarding({ onComplete, userEmail }: OnboardingProps) {
  const [step, setStep] = useState(0);
  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      {/* Progress bar */}
      <div className="h-1 bg-surface-100">
        <div
          className="h-full bg-brand transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Skip button */}
      <div className="flex justify-end px-6 pt-4">
        <button
          onClick={onComplete}
          className="text-xs text-ink-light hover:text-ink transition"
        >
          Skip setup
        </button>
      </div>

      {/* Content area */}
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-lg">
          {current.id === 'welcome' && <WelcomeStep />}
          {current.id === 'how-shield' && <ShieldStep />}
          {current.id === 'how-sender' && <SenderStep />}
          {current.id === 'install' && <InstallStep />}
          {current.id === 'team' && <TeamStep userEmail={userEmail} />}
          {current.id === 'ready' && <ReadyStep />}
        </div>
      </div>

      {/* Navigation */}
      <div className="px-6 pb-8 flex items-center justify-between max-w-lg mx-auto w-full">
        <button
          onClick={() => setStep(Math.max(0, step - 1))}
          className={`text-sm text-ink-muted hover:text-ink transition ${step === 0 ? 'invisible' : ''}`}
        >
          Back
        </button>

        {/* Step dots */}
        <div className="flex gap-1.5">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === step ? 'w-6 bg-brand' : i < step ? 'w-1.5 bg-brand/40' : 'w-1.5 bg-surface-200'
              }`}
            />
          ))}
        </div>

        <button
          onClick={() => isLast ? onComplete() : setStep(step + 1)}
          className="rounded-lg bg-brand px-6 py-2.5 text-sm font-medium text-white hover:bg-red-700 transition"
        >
          {isLast ? 'Get started' : 'Next'}
        </button>
      </div>
    </div>
  );
}

// --- Step Components ---

function WelcomeStep() {
  return (
    <div className="text-center">
      <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-brand/10">
        <Logo className="h-10 w-10 text-brand" />
      </div>
      <h2 className="text-2xl font-bold text-ink mb-2">Welcome to AAS Shield</h2>
      <p className="text-ink-muted leading-relaxed max-w-sm mx-auto">
        AAS detects passive-aggression, gaslighting, and toxic patterns in workplace emails — and helps you communicate better.
      </p>

      <div className="mt-8 grid grid-cols-2 gap-3 text-left">
        <div className="rounded-xl border border-surface-200 bg-surface-50 p-4">
          <div className="flex items-center gap-2 mb-2">
            <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4 text-brand">
              <path d="M8 1.5L3 4v3.5c0 3.4 2.13 6.6 5 7.5 2.87-.9 5-4.1 5-7.5V4L8 1.5z" stroke="currentColor" strokeWidth="1.2" fill="currentColor" fillOpacity="0.15" />
            </svg>
            <span className="text-xs font-bold text-ink">Shield</span>
          </div>
          <p className="text-xs text-ink-muted">Analyze emails you receive for hidden toxicity</p>
        </div>
        <div className="rounded-xl border border-surface-200 bg-surface-50 p-4">
          <div className="flex items-center gap-2 mb-2">
            <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4 text-sky-600">
              <path d="M11.5 2.5l2 2-8 8H3.5v-2l8-8z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="text-xs font-bold text-ink">Sender</span>
          </div>
          <p className="text-xs text-ink-muted">Check your drafts before you hit send</p>
        </div>
      </div>
    </div>
  );
}

function ShieldStep() {
  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10">
          <svg viewBox="0 0 16 16" fill="none" className="h-5 w-5 text-brand">
            <path d="M8 1.5L3 4v3.5c0 3.4 2.13 6.6 5 7.5 2.87-.9 5-4.1 5-7.5V4L8 1.5z" stroke="currentColor" strokeWidth="1.2" fill="currentColor" fillOpacity="0.15" />
          </svg>
        </div>
        <div>
          <h2 className="text-xl font-bold text-ink">Shield Mode</h2>
          <p className="text-sm text-ink-muted">Analyze emails you receive</p>
        </div>
      </div>

      {/* Example email analysis */}
      <div className="rounded-xl border border-surface-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-surface-100 bg-surface-50">
          <p className="text-xs text-ink-muted font-mono">From: your-colleague@work.com</p>
          <p className="text-xs text-ink font-medium mt-0.5">Re: Q3 Numbers</p>
        </div>
        <div className="px-4 py-3 text-xs text-ink font-mono leading-relaxed">
          <span className="bg-red-100 text-red-800 rounded px-0.5">As per my last email</span>, I still haven't received the numbers. I've <span className="bg-red-100 text-red-800 rounded px-0.5">copied your director</span> so we're all aligned.
        </div>
        <div className="px-4 py-3 border-t border-surface-100 flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-50">
            <span className="text-sm font-black text-red-700">72</span>
          </div>
          <div>
            <div className="flex gap-1">
              <span className="rounded-full bg-red-50 text-red-700 px-2 py-0.5 text-[10px] font-bold">Passive aggression</span>
              <span className="rounded-full bg-orange-50 text-orange-700 px-2 py-0.5 text-[10px] font-bold">CC intimidation</span>
            </div>
            <p className="text-[11px] text-ink-muted mt-0.5 italic">"As per my last email" = I'm frustrated you ignored me</p>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2 text-xs text-ink-muted">
        <svg className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
        </svg>
        <span>AAS translates what people <strong>really</strong> mean, suggests diplomatic replies, and rewrites the email without toxicity.</span>
      </div>
    </div>
  );
}

function SenderStep() {
  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-50">
          <svg viewBox="0 0 16 16" fill="none" className="h-5 w-5 text-sky-600">
            <path d="M11.5 2.5l2 2-8 8H3.5v-2l8-8z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div>
          <h2 className="text-xl font-bold text-ink">Sender Mode</h2>
          <p className="text-sm text-ink-muted">Check your drafts before sending</p>
        </div>
      </div>

      {/* Example draft check */}
      <div className="rounded-xl border border-sky-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-sky-100 bg-sky-50/50">
          <p className="text-xs text-sky-700 font-medium">Your draft</p>
        </div>
        <div className="px-4 py-3 text-xs text-ink font-mono leading-relaxed">
          Hey, I <span className="bg-yellow-100 text-yellow-800 rounded px-0.5">still haven't gotten</span> the Q3 numbers. <span className="bg-yellow-100 text-yellow-800 rounded px-0.5">Going forward, when I give you a deadline, stick to it.</span>
        </div>
        <div className="px-4 py-3 border-t border-sky-100">
          <p className="text-[11px] text-sky-700 font-medium mb-2">Better version:</p>
          <p className="text-xs text-ink leading-relaxed">
            Hi! I wanted to follow up on the Q3 numbers — could you share an updated timeline? I want to make sure we're aligned on expectations going forward.
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2 text-xs text-ink-muted">
        <svg className="h-4 w-4 text-sky-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.042 21.672L13.684 16.6m0 0l-2.51 2.225.569-9.47 5.227 7.917-3.286-.672zM12 2.25V4.5m5.834.166l-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243l-1.59-1.59" />
        </svg>
        <span>Catch unintentional passive-aggression before it reaches your recipient. Same facts, better delivery.</span>
      </div>
    </div>
  );
}

function InstallStep() {
  return (
    <div>
      <h2 className="text-xl font-bold text-ink mb-2">Install where you work</h2>
      <p className="text-sm text-ink-muted mb-6">Choose the platform that fits your workflow</p>

      <div className="space-y-3">
        <div className="rounded-xl border border-surface-200 bg-white p-4 flex items-center gap-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface-50">
            <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-ink">Gmail Sidebar</p>
            <p className="text-xs text-ink-muted">Analyze emails right in your inbox</p>
          </div>
          <span className="rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10px] font-bold">Available</span>
        </div>

        <div className="rounded-xl border border-dashed border-surface-200 bg-surface-50 p-4 flex items-center gap-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface-100">
            <svg className="h-5 w-5 text-surface-300" viewBox="0 0 24 24"><path fill="currentColor" d="M4 4h7.5v7.5H4V4zm8.5 0H20v7.5h-7.5V4zM4 12.5h7.5V20H4v-7.5zm8.5 0H20V20h-7.5v-7.5z"/></svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-ink-light">Outlook Add-in</p>
            <p className="text-xs text-surface-300">Coming soon</p>
          </div>
        </div>

        <div className="rounded-xl border border-surface-200 bg-white p-4 flex items-center gap-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface-50">
            <svg className="h-5 w-5 text-ink-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-ink">Chrome Extension</p>
            <p className="text-xs text-ink-muted">Works with any webmail</p>
          </div>
          <span className="rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10px] font-bold">Available</span>
        </div>

        <div className="rounded-xl border border-dashed border-surface-200 bg-surface-50 p-4 flex items-center gap-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface-100">
            <svg className="h-5 w-5 text-surface-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-ink-light">Mobile App</p>
            <p className="text-xs text-surface-300">Coming soon</p>
          </div>
        </div>
      </div>

      <p className="text-xs text-ink-light mt-4 text-center">
        You can also use the <strong>Try It</strong> page in this dashboard to test right away.
      </p>
    </div>
  );
}

function TeamStep({ userEmail }: { userEmail: string }) {
  const domain = userEmail.split('@')[1];

  return (
    <div>
      <h2 className="text-xl font-bold text-ink mb-2">Better together</h2>
      <p className="text-sm text-ink-muted mb-6">Teams of 3+ get discounted pricing</p>

      <div className="rounded-xl border border-surface-200 bg-white p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex -space-x-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className={`h-8 w-8 rounded-full border-2 border-white ${
                i === 0 ? 'bg-brand/20' : i === 1 ? 'bg-sky-100' : 'bg-emerald-100'
              } flex items-center justify-center`}>
                <span className="text-[10px] font-bold text-ink-muted">
                  {i === 0 ? 'Y' : i === 1 ? 'A' : 'B'}
                </span>
              </div>
            ))}
          </div>
          <span className="text-xs text-ink-muted">3+ members = team discount</span>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-surface-50 p-3">
            <p className="text-lg font-black text-ink">$7<span className="text-xs font-normal text-ink-muted">/seat/mo</span></p>
            <p className="text-[10px] text-ink-muted">Fortress <span className="line-through">$9</span></p>
          </div>
          <div className="rounded-lg bg-surface-50 p-3">
            <p className="text-lg font-black text-ink">$15<span className="text-xs font-normal text-ink-muted">/seat/mo</span></p>
            <p className="text-[10px] text-ink-muted">Citadel <span className="line-through">$19</span></p>
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
        <p className="text-xs text-emerald-700">
          <strong>Referral bonus:</strong> Share your referral link and both you and your friend get 1 month free when they upgrade.
        </p>
      </div>

      {domain && !['gmail.com', 'outlook.com', 'hotmail.com', 'yahoo.com'].includes(domain) && (
        <p className="text-xs text-ink-light mt-3 text-center">
          Invite colleagues from <strong>@{domain}</strong> from the Team page after setup.
        </p>
      )}
    </div>
  );
}

function ReadyStep() {
  return (
    <div className="text-center">
      <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-emerald-50">
        <svg className="h-10 w-10 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-ink mb-2">You're all set!</h2>
      <p className="text-ink-muted leading-relaxed max-w-sm mx-auto mb-6">
        You have <strong>10 free analyses per day</strong> to try both Shield and Sender modes.
      </p>

      <div className="space-y-2 text-left max-w-xs mx-auto">
        <div className="flex items-center gap-3 text-sm text-ink-muted">
          <span className="text-emerald-500">1.</span>
          <span>Go to <strong>Try It</strong> and paste an email</span>
        </div>
        <div className="flex items-center gap-3 text-sm text-ink-muted">
          <span className="text-emerald-500">2.</span>
          <span>Toggle between Shield and Sender modes</span>
        </div>
        <div className="flex items-center gap-3 text-sm text-ink-muted">
          <span className="text-emerald-500">3.</span>
          <span>Install an extension for automatic analysis</span>
        </div>
      </div>
    </div>
  );
}
